import React, { useState, useEffect } from 'react';
import {
  X,
  ThumbsDown,
  Search,
  ExternalLink,
  FileText,
  Ban,
  AlertCircle,
  Shield,
  Target,
  CheckCircle,
} from '../../../icons';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import type { VideoResult } from './types';
import { Tag_, RatingBadge, ScoreRing } from './primitives';

/* ─── HighlightedTranscript ──────────────────────────────────────────────── */

export function HighlightedTranscript({
  text,
  search,
  bannedWords,
  sensitiveWords,
  problematicPhrases,
}: {
  text: string;
  search: string;
  bannedWords: string[];
  sensitiveWords: string[];
  problematicPhrases: string[];
}) {
  if (!text) return <span className="text-muted-foreground">No transcript text available.</span>;

  type HType = 'search' | 'banned' | 'sensitive' | 'problematic';
  const highlights: Array<{ word: string; type: HType }> = [
    ...(search.trim() ? [{ word: search.trim(), type: 'search' as HType }] : []),
    ...bannedWords.map((w) => ({ word: w, type: 'banned' as HType })),
    ...sensitiveWords.map((w) => ({ word: w, type: 'sensitive' as HType })),
    ...problematicPhrases.map((w) => ({ word: w, type: 'problematic' as HType })),
  ].filter((h) => h.word.length > 0);

  const colorMap: Record<HType, { bg: string; color: string; border: string }> = {
    search: { bg: '#fef08a', color: '#854d0e', border: '#fde047' },
    banned: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
    sensitive: { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
    problematic: { bg: '#fafafa', color: '#737373', border: '#e5e5e5' },
  };

  if (!highlights.length) return <>{text}</>;

  const escaped = highlights.map((h) => h.word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, i) => {
        const match = highlights.find((h) => h.word.toLowerCase() === part.toLowerCase());
        if (match) {
          const c = colorMap[match.type];
          return (
            <mark
              key={i}
              style={{
                background: c.bg,
                color: c.color,
                border: `1px solid ${c.border}`,
                borderRadius: 3,
                padding: '0 2px',
                fontWeight: 700,
              }}
            >
              {part}
            </mark>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

/* ─── CommentsModal ──────────────────────────────────────────────────────── */

export function CommentsModal({ video, onClose }: { video: VideoResult; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const comments = (video.top_negative_comments || []).filter(
    (c) => !search || c.text.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex' }}>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }}
      />
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 500,
          background: '#fff',
          
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideIn 0.22s ease',
        }}
      >
        <div
          className="border-b border-border bg-muted" style={{ padding: '18px 20px' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                <div className="flex items-center justify-center rounded-lg border border-red-200 bg-red-50" style={{ width: 28, height: 28 }}>
                  <ThumbsDown size={13} className="text-red-600" />
                </div>
                <span className="text-body-md-semibold text-foreground">
                  Critical Comments
                </span>
              </div>
              <div
                className="text-muted-foreground"
                style={{
                  fontSize: 14,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
                }}
              >
                {video.title}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Tag_ color="#dc2626" bg="#fef2f2" border="#fecaca">
                  {video.top_negative_comments?.length || 0} flagged
                </Tag_>
                <Tag_ color="#737373" bg="#f5f5f5" border="#e5e5e5">
                  {video.comments_analyzed} analyzed
                </Tag_>
                <RatingBadge rating={video.flag} />
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Close"
              onClick={onClose}
              className="shrink-0 rounded-lg border border-border text-muted-foreground hover:bg-accent"
            >
              <X size={18} />
            </Button>
          </div>
          <div style={{ marginTop: 12, position: 'relative' }}>
            <Search
              size={13}
              className="text-muted-foreground"
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search comments…"
              className="w-full rounded-lg border-border bg-card pl-[30px] pr-2.5 py-1.5 text-body-sm-regular text-foreground outline-none"
            />
          </div>
        </div>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '12px 20px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {comments.length === 0 ? (
            <div className="text-center text-body-sm-regular text-muted-foreground" style={{ padding: 40 }}>
              {search ? 'No comments match your search' : 'No critical comments found'}
            </div>
          ) : (
            comments.map((c, i) => (
              <div
                key={i}
                style={{
                  background: '#FAFAFA',
                  border: '1px solid #E5E5E5',
                  borderRadius: 10,
                  padding: '12px 14px',
                }}
              >
                <p
                  className="text-foreground" style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.65 }}
                >
                  "{c.text}"
                </p>
                {c.likes > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 13,
                      color: '#d4d4d4',
                    }}
                  >
                    <ThumbsDown size={10} />
                    <span>{c.likes.toLocaleString()} likes</span>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #f5f5f5',
            background: '#fafafa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span className="text-caption-medium text-muted-foreground">Sorted by engagement</span>
          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 13,
              fontWeight: 600,
              color: '#404040',
              textDecoration: 'none',
              padding: '6px 12px',
              background: '#fafafa',
              borderRadius: 7,
              border: '1px solid #e5e5e5',
            }}
          >
            <ExternalLink size={11} /> View on YouTube
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─── TranscriptModal ────────────────────────────────────────────────────── */

export function TranscriptModal({ video, onClose }: { video: VideoResult; onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<'analysis' | 'transcript'>('analysis');
  const [search, setSearch] = useState('');
  const analysis = video.transcript_analysis;
  const transcript = video.transcript || '';

  const bannedWords = (analysis?.banned_keyword_matches || []).map((m) => m.word);
  const sensitiveWords = (analysis?.sensitive_topic_mentions || []).map((m) => m.topic);
  const problematicPhrases = (analysis?.problematic_phrases || []).map((m) => m.phrase);

  // No fabricated fallback — when the score is absent we hide the score UI.
  const scoreVal = analysis?.transcript_safety_score ?? null;
  const scoreColor =
    scoreVal == null ? '#737373' : scoreVal >= 70 ? '#059669' : scoreVal >= 45 ? '#d97706' : '#dc2626';
  const scoreBg =
    scoreVal == null ? '#fafafa' : scoreVal >= 70 ? '#f0fdf4' : scoreVal >= 45 ? '#fffbeb' : '#fef2f2';

  const totalIssues =
    (analysis?.banned_keyword_matches.length ?? 0) +
    (analysis?.sensitive_topic_mentions.length ?? 0) +
    (analysis?.problematic_phrases.length ?? 0) +
    (analysis?.brand_alignment_issues.length ?? 0);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex' }}>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }}
      />
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 579,
          background: '#fff',
          
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideIn 0.22s ease',
        }}
      >
        {/* Header */}
        <div
          className="border-b border-border bg-muted" style={{ padding: '16px 20px' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: '#FFFFFF',
                    border: '1px solid #E5E5E5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FileText size={16} color="#737373" />
                </div>
                <span className="text-body-md-semibold text-foreground">
                  Transcript Analysis
                </span>
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: '#171717',
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
                }}
              >
                {video.title}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  marginTop: 8,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {analysis && (
                  <>
                    {scoreVal != null && (
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: scoreColor,
                          background: scoreBg,
                          padding: '2px 9px',
                          borderRadius: 99,
                          border: `1px solid ${scoreColor}40`,
                        }}
                      >
                        Score: {scoreVal}/100
                      </span>
                    )}
                    {analysis.transcript_flag && <RatingBadge rating={analysis.transcript_flag} />}
                  </>
                )}
                {analysis?.word_count ? (
                  <Tag_ color="#737373" bg="#f5f5f5" border="#e5e5e5">
                    {analysis.word_count.toLocaleString()} words
                  </Tag_>
                ) : null}
                {totalIssues > 0 ? (
                  <Tag_ color="#dc2626" bg="#fef2f2" border="#fecaca">
                    {totalIssues} issue{totalIssues !== 1 ? 's' : ''} found
                  </Tag_>
                ) : (
                  analysis && (
                    <Tag_ color="#15803d" bg="#f0fdf4" border="#bbf7d0">
                      ✓ Clean
                    </Tag_>
                  )
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Close"
              onClick={onClose}
              className="shrink-0 rounded-lg border border-border text-muted-foreground hover:bg-accent"
            >
              <X size={18} />
            </Button>
          </div>

          {/* Tab bar */}
          <div
            style={{ display: 'flex', gap: 0, marginTop: 12, borderBottom: '1px solid #f5f5f5' }}
          >
            {(
              [
                ['analysis', 'Analysis'],
                ['transcript', 'Full Transcript'],
              ] as const
            ).map(([key, label]) => (
              <Button
                key={key}
                variant="ghost"
                onClick={() => setActiveTab(key)}
                style={{
                  padding: '0 0 8px',
                  marginRight: 20,
                  fontSize: 14,
                  fontWeight: 500,
                  borderBottom: activeTab === key ? '2px solid #171717' : '2px solid transparent',
                  borderRadius: 0,
                  background: 'none',
                  color: activeTab === key ? '#171717' : '#737373',
                  transition: 'color 0.12s',
                  height: 'auto',
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* ── Analysis tab ── */}
          {activeTab === 'analysis' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {!analysis ? (
                <div className="text-center text-muted-foreground" style={{ padding: 40 }}>
                  <FileText
                    size={28}
                    style={{ margin: '0 auto 10px', display: 'block', opacity: 0.4 }}
                  />
                  <div style={{ fontSize: 15, fontWeight: 600 }}>No analysis available</div>
                  <div style={{ fontSize: 14, marginTop: 4 }}>
                    Transcript could not be fetched for this video
                  </div>
                </div>
              ) : (
                <>
                  {/* Score hero */}
                  <div
                    style={{
                      background: scoreBg,
                      border: `1px solid ${scoreColor}30`,
                      borderRadius: 8,
                      padding: '16px 18px',
                      display: 'flex',
                      gap: 16,
                      alignItems: 'center',
                    }}
                  >
                    {scoreVal != null && <ScoreRing score={scoreVal} size={72} />}
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: scoreColor,
                          textTransform: 'uppercase',
                          letterSpacing: 0.8,
                          marginBottom: 5,
                        }}
                      >
                        Transcript Safety Score
                      </div>
                      <p className="text-foreground" style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65 }}>
                        {analysis.transcript_summary}
                      </p>
                    </div>
                  </div>

                  {/* Stats grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    {[
                      {
                        label: 'Banned',
                        count: analysis.banned_keyword_matches.length,
                        color: '#dc2626',
                      },
                      {
                        label: 'Sensitive',
                        count: analysis.sensitive_topic_mentions.length,
                        color: '#d97706',
                      },
                      {
                        label: 'Problematic',
                        count: analysis.problematic_phrases.length,
                        color: '#737373',
                      },
                      {
                        label: 'Brand Issues',
                        count: analysis.brand_alignment_issues.length,
                        color: '#737373',
                      },
                    ].map((s) => (
                      <div
                        key={s.label}
                        style={{
                          background: '#fafafa',
                          border: '1px solid #e5e5e5',
                          borderRadius: 8,
                          padding: '13px 15px',
                          textAlign: 'center',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 600,
                            color: s.color,
                            lineHeight: 1,
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {s.count}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: '#737373',
                            fontWeight: 500,
                            marginTop: 4,
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                          }}
                        >
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Banned keywords */}
                  {analysis.banned_keyword_matches.length > 0 && (
                    <div
                      className="bg-card"
                      style={{
                        border: '1px solid #fecaca',
                        borderRadius: 8,
                        padding: '16px',
                      }}
                    >
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 11 }}
                      >
                        <Ban size={13} className="text-red-600" />
                        <span className="text-body-sm-semibold text-red-600">
                          Banned Keywords Detected ({analysis.banned_keyword_matches.length})
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {analysis.banned_keyword_matches.map((m, i) => (
                          <div key={i} style={{ borderLeft: '3px solid #ef4444', paddingLeft: 11 }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                                marginBottom: 4,
                              }}
                            >
                              <span
                                className="rounded-full border border-red-200 bg-red-50 text-caption-medium text-red-600"
                                style={{ padding: '1px 8px' }}
                              >
                                {m.word}
                              </span>
                              {m.count > 1 && (
                                <span className="text-[10px] text-muted-foreground">
                                  mentioned ×{m.count}
                                </span>
                              )}
                            </div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: 13.5,
                                color: '#737373',
                                fontStyle: 'italic',
                                lineHeight: 1.55,
                              }}
                            >
                              "{m.context}"
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sensitive topics */}
                  {analysis.sensitive_topic_mentions.length > 0 && (
                    <div
                      className="bg-card"
                      style={{
                        border: '1px solid #fde68a',
                        borderRadius: 8,
                        padding: '16px',
                      }}
                    >
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 11 }}
                      >
                        <AlertCircle size={13} className="text-amber-500" />
                        <span className="text-body-sm-semibold text-amber-600">
                          Sensitive Topics Mentioned ({analysis.sensitive_topic_mentions.length})
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {analysis.sensitive_topic_mentions.map((m, i) => (
                          <div key={i} style={{ borderLeft: '3px solid #f59e0b', paddingLeft: 11 }}>
                            <span
                              style={{
                                fontSize: 13,
                                padding: '1px 8px',
                                borderRadius: 99,
                                background: '#fffbeb',
                                color: '#d97706',
                                border: '1px solid #fde68a',
                                fontWeight: 700,
                                display: 'inline-block',
                                marginBottom: 4,
                              }}
                            >
                              {m.topic}
                            </span>
                            <p
                              style={{
                                margin: 0,
                                fontSize: 13.5,
                                color: '#737373',
                                fontStyle: 'italic',
                                lineHeight: 1.55,
                              }}
                            >
                              "{m.context}"
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Problematic phrases */}
                  {analysis.problematic_phrases.length > 0 && (
                    <div
                      className="bg-card"
                      style={{
                        border: '1px solid #e5e5e5',
                        borderRadius: 8,
                        padding: '16px',
                      }}
                    >
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 11 }}
                      >
                        <Shield size={13} className="text-muted-foreground" />
                        <span className="text-body-sm-semibold text-foreground">
                          Problematic Phrases ({analysis.problematic_phrases.length})
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {analysis.problematic_phrases.map((p, i) => {
                          const sevColor =
                            p.severity === 'high'
                              ? '#dc2626'
                              : p.severity === 'medium'
                                ? '#d97706'
                                : '#737373';
                          const sevBg =
                            p.severity === 'high'
                              ? '#fef2f2'
                              : p.severity === 'medium'
                                ? '#fffbeb'
                                : '#fafafa';
                          return (
                            <div
                              key={i}
                              style={{ borderLeft: `3px solid ${sevColor}`, paddingLeft: 11 }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 7,
                                  marginBottom: 4,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 9,
                                    padding: '1px 6px',
                                    borderRadius: 4,
                                    background: sevBg,
                                    color: sevColor,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  {p.severity}
                                </span>
                                <span className="text-body-sm-semibold text-foreground">
                                  "{p.phrase}"
                                </span>
                              </div>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: 13,
                                  color: '#737373',
                                  lineHeight: 1.55,
                                }}
                              >
                                {p.reason}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Brand alignment issues */}
                  {analysis.brand_alignment_issues.length > 0 && (
                    <div
                      className="bg-card"
                      style={{
                        border: '1px solid #e5e5e5',
                        borderRadius: 8,
                        padding: '16px',
                      }}
                    >
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 11 }}
                      >
                        <Target size={13} className="text-muted-foreground" />
                        <span className="text-body-sm-semibold text-foreground">
                          Brand Alignment Issues ({analysis.brand_alignment_issues.length})
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {analysis.brand_alignment_issues.map((issue, i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              gap: 8,
                              alignItems: 'flex-start',
                              padding: '7px 10px',
                              background: '#fafafa',
                              borderRadius: 6,
                            }}
                          >
                            <span className="shrink-0 text-muted-foreground" style={{ marginTop: 1 }}>•</span>
                            <p
                              className="m-0 text-body-sm-regular text-foreground" style={{ lineHeight: 1.6 }}
                            >
                              {issue}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All clear */}
                  {totalIssues === 0 && (
                    <div
                      style={{
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: 14,
                        padding: '24px 20px',
                        textAlign: 'center',
                      }}
                    >
                      <CheckCircle
                        size={28}
                        color="#059669"
                        style={{ margin: '0 auto 10px', display: 'block' }}
                      />
                      <div className="text-body-md-semibold text-green-700">
                        No Issues Detected
                      </div>
                      <div className="text-body-sm-regular text-green-300" style={{ marginTop: 4 }}>
                        This transcript appears clean and brand-safe
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Full Transcript tab ── */}
          {activeTab === 'transcript' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Search */}
              <div style={{ position: 'relative' }}>
                <Search
                  size={13}
                  className="text-muted-foreground"
                  style={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search transcript…"
                  className="w-full rounded-lg border-border bg-muted pl-[30px] pr-2.5 py-2 text-body-sm-regular text-foreground outline-none"
                />
              </div>

              {/* Legend */}
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  padding: '8px 12px',
                  background: '#fafafa',
                  borderRadius: 8,
                  border: '1px solid #e5e5e5',
                  alignItems: 'center',
                }}
              >
                <span
                  className="text-muted-foreground"
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Legend:
                </span>
                {search.trim() && (
                  <span
                    style={{
                      fontSize: 10,
                      padding: '1px 7px',
                      borderRadius: 4,
                      background: '#fef08a',
                      color: '#854d0e',
                      border: '1px solid #fde047',
                    }}
                  >
                    Search match
                  </span>
                )}
                {bannedWords.length > 0 && (
                  <span
                    className="rounded-full border border-red-200 bg-red-50 text-[10px] text-red-600"
                    style={{ padding: '1px 7px' }}
                  >
                    Banned keyword
                  </span>
                )}
                {sensitiveWords.length > 0 && (
                  <span
                    className="rounded-full border border-amber-200 bg-amber-50 text-[10px] text-amber-600"
                    style={{ padding: '1px 7px' }}
                  >
                    Sensitive topic
                  </span>
                )}
                {problematicPhrases.length > 0 && (
                  <span
                    className="border border-border bg-muted text-muted-foreground"
                    style={{
                      fontSize: 10,
                      padding: '1px 7px',
                      borderRadius: 4,
                    }}
                  >
                    Problematic phrase
                  </span>
                )}
              </div>

              {/* Transcript text */}
              {transcript ? (
                <div
                  className="bg-card text-foreground"
                  style={{
                    border: '1px solid #e5e5e5',
                    borderRadius: 12,
                    padding: '16px 18px',
                    lineHeight: 1.85,
                    fontSize: 14.5,
                  }}
                >
                  <HighlightedTranscript
                    text={transcript}
                    search={search}
                    bannedWords={bannedWords}
                    sensitiveWords={sensitiveWords}
                    problematicPhrases={problematicPhrases}
                  />
                </div>
              ) : (
                <div className="text-center text-muted-foreground" style={{ padding: 40 }}>
                  <FileText
                    size={28}
                    style={{ margin: '0 auto 10px', display: 'block', opacity: 0.4 }}
                  />
                  <div style={{ fontSize: 15, fontWeight: 600 }}>Transcript unavailable</div>
                  <div style={{ fontSize: 14, marginTop: 4 }}>
                    This video may not have captions enabled
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #F5F5F5',
            background: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span className="text-muted-foreground" style={{ fontSize: 12.5 }}>
            {analysis?.word_count ? `${analysis.word_count.toLocaleString()} words` : 'N/A'} ·
            Auto-generated captions
          </span>
          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 14,
              fontWeight: 500,
              color: '#000000',
              textDecoration: 'none',
              padding: '6px 12px',
              background: '#FFFFFF',
              borderRadius: 7,
              border: '1px solid #A3A3A3',
            }}
          >
            <ExternalLink size={11} /> Open on YouTube
          </a>
        </div>
      </div>
    </div>
  );
}
